/**
 * @override `@salesforce/retail-react-app/app/utils/url`
 *
 * Centralised URL builder for King Living. Adds `c_productPageURL` (Contentful-managed slug)
 * support to `productUrlBuilder` while preserving the upstream contract — the resolver plugin
 * routes every existing call site (`@salesforce/retail-react-app/app/utils/url` imports across
 * the king-living package + both vendor packages) to this module automatically, so no caller
 * needs to switch import paths.
 *
 * Why a custom slug?
 *   The PWA's dynamic-page router (`pages/dynamic-page/index.jsx`) resolves every `/product/*`
 *   URL via a Contentful slug match — there is no SFCC `/product/:id` fallback for King Living
 *   sites. Marketing/SEO drive PDP URLs from Contentful, and the slug is mirrored onto each
 *   product as `c_productPageURL` so the same value can be read at runtime everywhere a link
 *   gets rendered (PLP tile, header search, cart line item, recommenders, etc.).
 *
 *   The dynamic-page resolver decides which Contentful entry to load from the slug; if a product
 *   has no `c_productPageURL` configured we fall back to `/product/{id}`, which the resolver
 *   matches against a Contentful entry whose slug is `product/{id}` (the default naming
 *   convention).
 *
 * Read priority (`getCustomProductPageURL`):
 *   1. `product.c_productPageURL`                       — top-level (getProduct/PDP, basket line items)
 *   2. `product.representedProduct.c_productPageURL`    — nested (productSearch hit shape)
 *   3. (none) — caller falls back to upstream `/product/{id}`
 *
 * Format normalisation:
 *   - `'product/foo-bar'`       → `/product/foo-bar`   (leading slash added)
 *   - `'/product/foo-bar'`      → `/product/foo-bar`   (already absolute path; preserved)
 *   - `'https://kingliving…/p'` → `'https://kingliving…/p'` (absolute URL; preserved as-is for
 *     cross-region links)
 *   - empty / non-string         → resolver returns `''`, builder falls through to default
 *
 * Compatible input shapes:
 *   - SCAPI hit            `{productId, representedProduct: {c_productPageURL?}}`
 *   - getProduct response  `{id, c_productPageURL?}`
 *   - Basket line item     `{productId, c_productPageURL?}` (slug is captured at add-to-cart)
 *   - Synthetic `{id}`     legacy callers passing only the id — fall through to default builder
 *
 * KINDW-99 / centralisation request.
 */

import {productUrlBuilder as defaultProductUrlBuilder} from '@salesforce/retail-react-app/app/utils/url';
import {getParamsFromPath} from '@salesforce/retail-react-app/app/utils/site-utils';

export * from '@salesforce/retail-react-app/app/utils/url';

const ABSOLUTE_URL_RE = /^https?:\/\//i;

/**
 * Read the configured slug off a product/line-item shape, walking the SCAPI variants. Returns
 * `''` when nothing is configured so callers can use `||` chaining without truthy-guarding.
 *
 * @param {object} [product]
 * @returns {string}
 */
export const getCustomProductPageURL = (product) => {
  if (!product || typeof product !== 'object') return '';

  return (
    product.c_productPageURL ??
    product.representedProduct?.c_productPageURL ??
    ''
  );
};

/**
 * Convert a configured slug into an app-relative path.
 *
 * @param {string} raw
 * @returns {string}
 */
const normaliseSlug = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  const trimmed = raw.trim();

  if (!trimmed) return '';
  if (ABSOLUTE_URL_RE.test(trimmed)) return trimmed;

  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

/**
 * Given a product, return an href to its PDP. Honours `c_productPageURL` when configured;
 * otherwise falls back to the upstream `/product/{id}` builder.
 *
 * Identical signature to upstream — every existing `productUrlBuilder({id})` call site keeps
 * working unchanged. To benefit from the slug feature, pass the full product object instead of
 * a synthetic `{id}` so the helper can read `c_productPageURL` from it.
 *
 * @param {object} product
 * @returns {string}
 */
export const productUrlBuilder = (product) => {
  const slug = normaliseSlug(getCustomProductPageURL(product));

  if (slug) return slug;

  // Normalise input shape for the fallback so basket-line-item / search-hit objects (which use
  // `productId` rather than `id`) keep working without each caller having to remap.
  const id = product?.id ?? product?.productId;

  return defaultProductUrlBuilder({id});
};

/*
 * Remove locale from a given url path, to be used when site is implied by host.
 * Site segment is preserved (KINDW-1680).
 *
 * @param {string} pathName - The part of url to have locale removed from
 * @returns {string} - the path after locale has been removed
 * @example
 * import {removeLocaleFromPath} from '@amblique/king-living/app/utils/url'
 *
 * removeLocaleFromPath('/fr/account/wishlist')
 * // returns '/account/wishlist'
 */
export const removeLocaleFromPath = (pathName = '') => {
  const {localeRef} = getParamsFromPath(pathName);

  if (localeRef) {
    pathName = pathName.replace(new RegExp(`/${localeRef}`, 'g'), '');
  }

  return pathName;
};
